import { useEffect, useRef, useState } from "react";
import { tgCode, tgRevoke, tgStatus } from "../api";
import { fmtSync } from "../format";
import { MOCK } from "../mock";
import { drawQR } from "../qr";
import type { TgStatus } from "../types";
import { SendIcon } from "./icons";

/** Links this employee's Telegram to the same identity-bound session. */
export function Telegram() {
  const [status, setStatus] = useState<TgStatus>({ linked: false });
  const [note, setNote] = useState<string | null>(null);
  const [link, setLink] = useState<{ url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const mockLinked = useRef(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const refresh = async () => setStatus(await tgStatus(mockLinked.current));
  useEffect(() => {
    void refresh();
  }, []);

  // The QR is painted once the canvas and the code both exist.
  useEffect(() => {
    if (link && canvasRef.current) drawQR(canvasRef.current, link.url);
  }, [link]);

  async function connect() {
    setBusy(true);
    setNote(null);
    try {
      const d = await tgCode();
      setLink({ url: d.url });
    } catch (e) {
      setNote(String((e as Error).message || e));
    }
    setBusy(false);
  }

  async function revoke() {
    setBusy(true);
    if (MOCK) mockLinked.current = false;
    else await tgRevoke();
    setLink(null);
    setBusy(false);
    void refresh();
  }

  const since = fmtSync(status.linked_at);
  const badge = note
    ? note
    : status.linked
      ? since === "pending"
        ? "Linked"
        : "Linked · since " + since
      : "Not linked";

  return (
    <div className="tg">
      <div className="icon">
        <SendIcon />
      </div>
      <div className="body">
        <h3>Connect Telegram</h3>
        <p>
          Ask about your leave, MC or payslip straight from Telegram — same assistant, same
          identity guarantees.
        </p>
        <span className={status.linked && !note ? "status on" : "status"}>{badge}</span>
        <div className="actions">
          {!status.linked && (
            <button className="check" onClick={connect} disabled={busy}>
              Connect Telegram
            </button>
          )}
          {status.linked && (
            <button className="ghost" onClick={revoke} disabled={busy}>
              Revoke link
            </button>
          )}
        </div>
        {link && !status.linked && (
          <div className="tg-link">
            <canvas
              ref={canvasRef}
              width={0}
              height={0}
              aria-label="QR code for the Telegram link"
            />
            <div className="deets">
              Scan with your phone, or open{" "}
              <a href={link.url} target="_blank" rel="noopener">
                {link.url.replace("https://", "")}
              </a>
              <div className="exp">The link works once and expires in 10 minutes.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
